/*****************************************************
Naam: CustomerNewRequest.jsx
Locatie: src/pages/CustomerNewRequest.jsx
Datum: 2026-01-25
Versie: 2.0.0
Auteur: Claude
******************************************************
BESCHRIJVING:
Customer nieuwe aanvraag formulier - volledig herschreven met
dezelfde look & feel als het dashboard (AanvraagInkoopModal).

CHANGELOG v2.0.0 (2026-01-25):
- NIEUW: Volledige goederen invoer (5 rijen)
- NIEUW: Services checkboxes (11 opties)
- NIEUW: Bijzonderheden velden voor laad/los
- NIEUW: CustomerAdressenPanel component
- NIEUW: CustomerGegevensPanel component
- NIEUW: Dashboard-style panel styling
- NIEUW: "= Mijn adres" kopieer functie
- NIEUW: Auto-fill afmetingen bij pallet selectie
- NIEUW: Opmerkingen veld
- GEWIJZIGD: Volledig nieuwe UI matching dashboard

FEATURES:
- Adres invoer met alle velden
- 5-rij goederen tabel
- 11 services checkboxes
- Referenties en opmerkingen
- Validatie met feedback
- Success/error handling
- Responsive design

API ENDPOINT:
POST /api/customer/aanvraag
******************************************************/

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ChevronLeft,
  Send,
  CheckCircle,
  AlertCircle,
  Loader,
  FileText,
  Home
} from 'lucide-react';

// Customer Portal Components
import CustomerAdressenPanel from '../components/CustomerPortal/CustomerAdressenPanel';
import CustomerGegevensPanel from '../components/CustomerPortal/CustomerGegevensPanel';

// ============================================
// INITIAL STATE HELPERS
// ============================================

const createEmptyFormData = () => ({
  // Laadadres
  laadAdres: '',
  laadDatum: '',
  laadPostcode: '',
  laadPlaats: '',
  laadLand: '',
  laadBijzonderheden: '',
  // Losadres
  losAdres: '',
  losDatum: '',
  losPostcode: '',
  losPlaats: '',
  losLand: '',
  losBijzonderheden: '',
  losContactpersoon: '',
  losTelefoon: '',
  laadTijdVan: '',
  laadTijdTot: '',
  losTijdVan: '',
  losTijdTot: '',
  // Overig
  referentie1: '',
  referentie2: '',
  opmerkingen: '',
  // v7.0.0: Zendingdetails
  incoterm: '',
  isB2C: false,
  goederenwaarde: '',
  aantalHsCodes: ''
});

const createEmptyGoederen = () => Array(5).fill(null).map(() => ({
  aantal: '',
  soort: '',
  omschrijving: '',
  stackable: '--',
  lengte: '',
  breedte: '',
  hoogte: '',
  gewicht: ''
}));

const createEmptyServices = () => ({
  serviceExpress: false,
  serviceParcel: false,
  serviceGroupage: false,
  serviceLTL: false,
  serviceFTL: false,
  serviceCustoms: false,
  serviceKooiaap: false,
  serviceKlep: false,
  serviceLastMile: false,
  serviceADR: false,
  serviceGeconditioneerd: false
});

// ============================================
// MAIN COMPONENT
// ============================================

const CustomerNewRequest = ({ onClose = null, onSuccess = null, initialAanvraag = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Form state
  const [formData, setFormData] = useState(createEmptyFormData());
  const [goederen, setGoederen] = useState(createEmptyGoederen());
  const [services, setServices] = useState(createEmptyServices());
  
  // Data state
  const [landen, setLanden] = useState([]);
  const [customerAddress, setCustomerAddress] = useState(null);
  const [klantProfiel, setKlantProfiel] = useState({ standaardIncoterm: 'DAP', standaardHsCodes: '1', goederenwaardeVerplicht: false });

  // Draggable modal state
  const [dragPos, setDragPos] = useState({ x: Math.max(0, (window.innerWidth - 1100) / 2), y: 40 });
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const handleDragStart = useCallback((e) => {
    if (e.button !== 0) return;
    setIsDragging(true);
    dragOffset.current = { x: e.clientX - dragPos.x, y: e.clientY - dragPos.y };
  }, [dragPos]);

  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    setDragPos({ x: e.clientX - dragOffset.current.x, y: e.clientY - dragOffset.current.y });
  }, [isDragging]);

  const handleDragEnd = useCallback(() => setIsDragging(false), []);

  // Prefill formulier als een bestaande aanvraag herhaald wordt
  useEffect(() => {
    if (!initialAanvraag) return;
    setFormData(prev => ({
      ...prev,
      laadAdres        : initialAanvraag.LaadAdres || '',
      laadPostcode     : initialAanvraag.LaadPostcode || '',
      laadPlaats       : initialAanvraag.LaadPlaats || '',
      laadLand         : initialAanvraag.LaadLand || '',
      laadBijzonderheden: initialAanvraag.LaadBijzonderheden || '',
      losAdres         : initialAanvraag.LosAdres || '',
      losPostcode      : initialAanvraag.LosPostcode || '',
      losPlaats        : initialAanvraag.LosPlaats || '',
      losLand          : initialAanvraag.LosLand || '',
      losBijzonderheden: initialAanvraag.LosBijzonderheden || '',
      losContactpersoon: initialAanvraag.LosContactpersoon || '',
      losTelefoon: initialAanvraag.LosTelefoon || '',
      laadTijdVan: initialAanvraag.LaadTijdVan || '',
      laadTijdTot: initialAanvraag.LaadTijdTot || '',
      losTijdVan: initialAanvraag.LosTijdVan || '',
      losTijdTot: initialAanvraag.LosTijdTot || '',
      referentie1: '', referentie2: '',
    }));
    const newGoederen = createEmptyGoederen();
    for (let i = 1; i <= 5; i++) {
      const soort = initialAanvraag[`Goed${i}_Soort`];
      if (soort) {
        newGoederen[i-1] = {
          aantal      : initialAanvraag[`Goed${i}_Aantal`] || 0,
          soort,
          omschrijving: initialAanvraag[`Goed${i}_Omschrijving`] || '',
          stackable   : initialAanvraag[`Goed${i}_Stackable`] || '--',
          lengte      : initialAanvraag[`Goed${i}_Lengte`] || 0,
          breedte     : initialAanvraag[`Goed${i}_Breedte`] || 0,
          hoogte      : initialAanvraag[`Goed${i}_Hoogte`] || 0,
          gewicht     : initialAanvraag[`Goed${i}_Gewicht`] || 0,
        };
      }
    }
    setGoederen(newGoederen);
    setServices({
      serviceExpress  : initialAanvraag.Express  === 'J',
      serviceParcel   : initialAanvraag.Parcel   === 'J',
      serviceGroupage : initialAanvraag.Groupage === 'J',
      serviceLTL      : initialAanvraag.LTL      === 'J',
      serviceFTL      : initialAanvraag.FTL      === 'J',
      serviceCustoms  : initialAanvraag.Customs  === 'J',
      serviceKooiaap  : initialAanvraag.Kooiaap  === 'J',
      serviceKlep     : initialAanvraag.Klep     === 'J',
    });
  }, [initialAanvraag]);

  useEffect(() => {
    if (!isDragging) return;
    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', handleDragEnd);
    return () => {
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);
  const [losAdressen, setLosAdressen] = useState([]);
  const [bestaandeAanvragen, setBestaandeAanvragen] = useState([]);
  const [showKopieerPanel, setShowKopieerPanel] = useState(false);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [isVasteZending, setIsVasteZending] = useState(false);
  const [vasteFrequentie, setVasteFrequentie] = useState('wekelijks');
  const [vasteWekelijkseDagen, setVasteWekelijkseDagen] = useState(['maandag']);
  const [vasteMaandelijkseDatum, setVasteMaandelijkseDatum] = useState(1);
  const [vasteDagenVoorLaad, setVasteDagenVoorLaad] = useState(3);
  const [vasteEmailMelding, setVasteEmailMelding] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  // ============================================
  // INITIALIZATION
  // ============================================

  useEffect(() => {
    checkAuth();
    loadLanden();
    loadCustomerData();
    loadLosAdressen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Kopieer aanvraag: vul formulier met data uit sessionStorage
  useEffect(() => {
    let kopieer = null;
    try {
      const stored = sessionStorage.getItem('kopieerAanvraag');
      if (stored) {
        kopieer = JSON.parse(stored);
        sessionStorage.removeItem('kopieerAanvraag');
      }
    } catch (e) {
      console.error('Kopieer laden mislukt:', e);
    }
    // Fallback: ook location.state proberen
    if (!kopieer) kopieer = location.state?.kopieerAanvraag;
    if (!kopieer) return;

    setFormData(prev => ({
      ...prev,
      laadAdres: kopieer.LaadAdres || '',
      laadPostcode: kopieer.LaadPostcode || '',
      laadPlaats: kopieer.LaadPlaats || '',
      laadLand: kopieer.LaadLand || '',
      laadBijzonderheden: kopieer.LaadBijzonderheden || '',
      losAdres: kopieer.LosAdres || '',
      losPostcode: kopieer.LosPostcode || '',
      losPlaats: kopieer.LosPlaats || '',
      losLand: kopieer.LosLand || '',
      losBijzonderheden: kopieer.LosBijzonderheden || '',
      referentie1: '',
      referentie2: ''
    }));

    // Kopieer goederen
    if (kopieer.Goederen && kopieer.Goederen.length > 0) {
      const newGoederen = createEmptyGoederen();
      kopieer.Goederen.forEach((g, idx) => {
        if (idx < 5) {
          newGoederen[idx] = {
            aantal: g.Aantal || 0,
            soort: g.Soort || '',
            omschrijving: g.Omschrijving || '',
            stackable: g.Stackable || '--',
            lengte: g.Lengte || 0,
            breedte: g.Breedte || 0,
            hoogte: g.Hoogte || 0,
            gewicht: g.Gewicht || 0
          };
        }
      });
      setGoederen(newGoederen);
    }

    // Kopieer services
    const svc = {};
    ['Express','Parcel','Groupage','LTL','FTL','Customs','Kooiaap','Klep','LastMile','ADR','Geconditioneerd'].forEach(s => {
      svc[`service${s}`] = kopieer[s] === 'J' || kopieer[`Service${s}`] === 'J';
    });
    setServices(prev => ({ ...prev, ...svc }));

    window.history.replaceState({}, document.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch('/api/customer/check-auth');
      const data = await response.json();
      
      if (!data.authenticated) {
        navigate('/customer/login');
      }
    } catch (err) {
      console.error('Auth check error:', err);
      navigate('/customer/login');
    }
  };

  const loadLanden = async () => {
    try {
      const response = await fetch('/api/customer/landen');
      const result = await response.json();
      
      if (result.success && result.data) {
        // Extract land names - kolom heet 'Land' (PascalCase)
        const landNames = result.data.map(l => l.Land).filter(Boolean);
        setLanden(landNames);
      }
    } catch (err) {
      console.error('Load landen error:', err);
    }
  };

  const loadCustomerData = async () => {
    try {
      const response = await fetch('/api/customer/account', { credentials: 'include' });
      const data = await response.json();
      
      if (data.success && data.account) {
        setCustomerAddress({
          adres: data.account.Adres || data.account.adres,
          postcode: data.account.Postcode || data.account.postcode,
          plaats: data.account.Plaats || data.account.plaats,
          land: data.account.Land || data.account.land
        });
        // v3.0.0: Laad zendingprofiel en vul automatisch in
        const profiel = {
          standaardIncoterm: data.account.standaardIncoterm || 'DAP',
          standaardHsCodes: data.account.standaardHsCodes || '1',
          goederenwaardeVerplicht: data.account.goederenwaardeVerplicht === true
        };
        setKlantProfiel(profiel);
        handleFormDataChange({
          incoterm: profiel.standaardIncoterm,
          aantalHsCodes: profiel.standaardHsCodes
        });
      }
    } catch (err) {
      console.error('Load customer data error:', err);
    }
  };

  const loadLosAdressen = async () => {
    try {
      const response = await fetch('/api/customer/aanvragen', { credentials: 'include' });
      const data = await response.json();
      if (data.success && data.aanvragen) {
        setBestaandeAanvragen(data.aanvragen);
        const seen = new Set();
        const adressen = [];
        data.aanvragen.forEach(a => {
          if (a.LosPlaats || a.LosAdres) {
            const key = `${a.LosAdres || ''}|${a.LosPostcode || ''}|${a.LosPlaats || ''}|${a.LosLand || ''}`;
            if (!seen.has(key)) {
              seen.add(key);
              adressen.push({
                LosAdres: a.LosAdres || '',
                LosPostcode: a.LosPostcode || '',
                LosPlaats: a.LosPlaats || '',
                LosLand: a.LosLand || ''
              });
            }
          }
        });
        setLosAdressen(adressen);
      }
    } catch (err) {
      console.error('Load losAdressen error:', err);
    }
  };

  // ============================================
  // HANDLERS
  // ============================================

  // ============================================
  // KOPIEER BESTAANDE AANVRAAG
  // ============================================

  const handleKopieerAanvraag = (aanvraagId) => {
    const aanvraag = bestaandeAanvragen.find(a => a.AanvraagID === aanvraagId);
    if (!aanvraag) return;

    setFormData(prev => ({
      ...prev,
      laadAdres: aanvraag.LaadAdres || '',
      laadPostcode: aanvraag.LaadPostcode || '',
      laadPlaats: aanvraag.LaadPlaats || '',
      laadLand: aanvraag.LaadLand || '',
      laadBijzonderheden: aanvraag.LaadBijzonderheden || '',
      losAdres: aanvraag.LosAdres || '',
      losPostcode: aanvraag.LosPostcode || '',
      losPlaats: aanvraag.LosPlaats || '',
      losLand: aanvraag.LosLand || '',
      losBijzonderheden: aanvraag.LosBijzonderheden || '',
      referentie1: '',
      referentie2: ''
    }));

    const newGoederen = createEmptyGoederen();
    for (let i = 1; i <= 5; i++) {
      const soort = aanvraag[`Goed${i}_Soort`];
      if (soort) {
        newGoederen[i-1] = {
          aantal: aanvraag[`Goed${i}_Aantal`] || 0,
          soort: soort,
          omschrijving: aanvraag[`Goed${i}_Omschrijving`] || '',
          stackable: aanvraag[`Goed${i}_Stackable`] || '--',
          lengte: aanvraag[`Goed${i}_Lengte`] || 0,
          breedte: aanvraag[`Goed${i}_Breedte`] || 0,
          hoogte: aanvraag[`Goed${i}_Hoogte`] || 0,
          gewicht: aanvraag[`Goed${i}_Gewicht`] || 0
        };
      }
    }
    setGoederen(newGoederen);

    // Kopieer services
    setServices({
      serviceExpress: aanvraag.Express === 'J',
      serviceParcel: aanvraag.Parcel === 'J',
      serviceGroupage: aanvraag.Groupage === 'J',
      serviceLTL: aanvraag.LTL === 'J',
      serviceFTL: aanvraag.FTL === 'J',
      serviceCustoms: aanvraag.Customs === 'J',
      serviceKooiaap: aanvraag.Kooiaap === 'J',
      serviceKlep: aanvraag.Klep === 'J',
      serviceLastMile: aanvraag.LastMile === 'J',
      serviceADR: aanvraag.ADR === 'J',
      serviceGeconditioneerd: aanvraag.Geconditioneerd === 'J'
    });
    setShowKopieerPanel(false);
  };

  const handleFormDataChange = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
    // Clear validation errors for changed fields
    const clearedErrors = { ...validationErrors };
    Object.keys(updates).forEach(key => delete clearedErrors[key]);
    setValidationErrors(clearedErrors);
    setError('');
  };

  const handleGoederenChange = (rowIndex, field, value) => {
    setGoederen(prev => {
      const updated = [...prev];
      updated[rowIndex] = { ...updated[rowIndex], [field]: value };
      return updated;
    });
  };

  const handleServiceChange = (serviceName, value) => {
    setServices(prev => ({ ...prev, [serviceName]: value }));
  };

  // ============================================
  // VALIDATION
  // ============================================

  const validateForm = () => {
    const errors = {};
    
    // Laadadres: adres OF (plaats + land) is voldoende
    const heeftLaadLocatie = formData.laadAdres || (formData.laadPlaats && formData.laadLand);
    if (!heeftLaadLocatie) {
      if (!formData.laadPlaats) errors.laadPlaats = 'Laadplaats is verplicht';
      if (!formData.laadLand) errors.laadLand = 'Laadland is verplicht';
    }
    if (!formData.laadLand) errors.laadLand = 'Laadland is verplicht';

    // Losadres: adres OF (plaats + land) is voldoende
    const heeftLosLocatie = formData.losAdres || (formData.losPlaats && formData.losLand);
    if (!heeftLosLocatie) {
      if (!formData.losPlaats) errors.losPlaats = 'Losplaats is verplicht';
      if (!formData.losLand) errors.losLand = 'Losland is verplicht';
    }
    if (!formData.losLand) errors.losLand = 'Losland is verplicht';
    
    // Check if at least one goed has data
    const hasGoederen = goederen.some(g => g.aantal || g.soort || g.omschrijving);
    if (!hasGoederen) {
      errors.goederen = 'Voer minimaal één goederenregel in';
    }

    // ── Afmetingen validatie: elke ingevulde goederenregel moet L×B×H hebben ──
    const goederenIngevuld = goederen.filter(g => g.soort || g.omschrijving || g.aantal);
    goederenIngevuld.forEach((g, i) => {
      const rij = goederen.indexOf(g) + 1;
      const ontbrekend = [];
      if (!g.lengte  || String(g.lengte).trim()  === '') ontbrekend.push('lengte');
      if (!g.breedte || String(g.breedte).trim() === '') ontbrekend.push('breedte');
      if (!g.hoogte  || String(g.hoogte).trim()  === '') ontbrekend.push('hoogte');
      if (ontbrekend.length > 0) {
        errors[`goed_afmeting_${rij}`] = `Rij ${rij} (${g.omschrijving || g.soort || ''}): ${ontbrekend.join(', ')} ontbreekt`;
      }
    });
    // ── Einde afmetingen validatie ──────────────────────────────────────────────

    // Date validation
    if (formData.laadDatum && formData.losDatum) {
      if (new Date(formData.losDatum) < new Date(formData.laadDatum)) {
        errors.losDatum = 'Losdatum kan niet voor laaddatum liggen';
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ============================================
  // SUBMIT
  // ============================================

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setError('Controleer de gemarkeerde velden');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      // Build request payload
      const payload = {
        ...formData,
        goederen: goederen.filter(g => g.aantal || g.soort || g.omschrijving),
        services: Object.entries(services)
          .filter(([_, value]) => value)
          .map(([key]) => key)
      };

      const response = await fetch('/api/customer/aanvraag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        // Sla vaste zending op als aangevinkt
        if (isVasteZending) {
          try {
            await fetch('/api/vaste-zendingen', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({
                naam           : `${formData.laadPlaats || ''} → ${formData.losPlaats || ''}`,
                frequentie     : vasteFrequentie,
                wekelijkse_dagen: vasteWekelijkseDagen,
                maandelijkse_datum: vasteMaandelijkseDatum,
                dagen_voor_laad: vasteDagenVoorLaad,
                laad_adres     : formData.laadAdres || '',
                laad_postcode  : formData.laadPostcode || '',
                laad_plaats    : formData.laadPlaats || '',
                laad_land      : formData.laadLand || 'Nederland',
                los_adres      : formData.losAdres || '',
                los_postcode   : formData.losPostcode || '',
                los_plaats     : formData.losPlaats || '',
                los_land       : formData.losLand || 'Nederland',
                goederen       : goederen.filter(g => g.soort),
                services,
                referentie1    : formData.referentie1 || '',
                email_melding  : vasteEmailMelding ? 1 : 0,
                aangemaakt_door: 'klant',
              }),
            });
          } catch(e) { console.error('Vaste zending opslaan mislukt:', e); }
        }
        // Reset form after 3 seconds and navigate
        setTimeout(() => {
          if (onClose) onClose(); else navigate('/customer/dashboard');
        }, 3000);
      } else {
        setError(typeof data.error === 'object' ? (data.error.message || 'Er is iets misgegaan bij het indienen') : (data.error || 'Er is iets misgegaan bij het indienen'));
      }
    } catch (err) {
      console.error('Submit error:', err);
      setError('Netwerkfout. Controleer je verbinding en probeer het opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData(createEmptyFormData());
    setGoederen(createEmptyGoederen());
    setServices(createEmptyServices());
    setValidationErrors({});
    setError('');
    setSuccess(false);
  };

  // ============================================
  // RENDER
  // ============================================

  // Inhoud van het formulier (gedeeld tussen modal en standalone)
  const formContent = (
    <>
      {/* ========== MAIN CONTENT ========== */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success Message */}
        {success && (
          <div className="mb-6 p-6 bg-green-50 border-2 border-green-200 flex items-center gap-4 animate-fade-in">
            <div className="p-3 bg-green-100 rounded-full">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <div>
              <p className="font-bold text-green-800 text-lg">Aanvraag succesvol ingediend!</p>
              <p className="text-green-700">
                We gaan direct voor je aan de slag. Je wordt doorgestuurd naar het dashboard...
              </p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">

          {/* ========== KOPIEER BESTAANDE AANVRAAG ========== */}
          {bestaandeAanvragen.length > 0 && (
            <div className="bg-white border border-blue-200 shadow-sm">
              <button
                type="button"
                onClick={() => setShowKopieerPanel(!showKopieerPanel)}
                className="w-full flex items-center justify-between px-6 py-4 hover:bg-blue-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText className="w-5 h-5 text-blue-700" />
                  </div>
                  <div className="text-left">
                    <span className="font-semibold text-blue-900 text-lg">Kopieer bestaande aanvraag</span>
                    <p className="text-sm text-gray-500">Vul het formulier met gegevens van een eerdere aanvraag</p>
                  </div>
                </div>
                <ChevronLeft className={`w-5 h-5 text-gray-400 transition-transform ${showKopieerPanel ? '-rotate-90' : ''}`} />
              </button>
              
              {showKopieerPanel && (
                <div className="border-t border-blue-100 px-6 py-4 bg-blue-50/30">
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-2 py-2 text-left font-semibold text-gray-700 w-24">ID</th>
                          <th className="px-2 py-2 text-left font-semibold text-gray-700">Route</th>
                          <th className="px-2 py-2 text-left font-semibold text-gray-700 w-24">Datum</th>
                          <th className="px-2 py-2 text-left font-semibold text-gray-700">Goederen</th>
                          <th className="px-2 py-2 w-16"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {bestaandeAanvragen.map((a) => {
                          const goederenTekst = [1,2,3,4,5]
                            .map(n => {
                              const soort = a[`Goed${n}_Soort`];
                              const aantal = a[`Goed${n}_Aantal`];
                              return soort ? `${aantal || ''} ${soort}`.trim() : null;
                            })
                            .filter(Boolean)
                            .join(', ');
                          return (
                            <tr key={a.AanvraagID} className="hover:bg-blue-50 cursor-pointer" onClick={() => handleKopieerAanvraag(a.AanvraagID)}>
                              <td className="px-2 py-2 font-semibold text-blue-900 whitespace-nowrap">#{a.AanvraagID}</td>
                              <td className="px-2 py-2 text-gray-700 whitespace-nowrap">
                                {a.LaadPlaats || ''} → {a.LosPlaats || ''}
                              </td>
                              <td className="px-2 py-2 text-gray-600 whitespace-nowrap">{a.Datum}</td>
                              <td className="px-2 py-2 text-gray-600">{goederenTekst || '-'}</td>
                              <td className="px-2 py-2 text-center">
                                <span className="inline-flex px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 transition-colors">
                                  Kopieer
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Adressen Panel */}
          <CustomerAdressenPanel
            formData={formData}
            landen={landen}
            customerAddress={customerAddress}
            losAdressen={losAdressen}
            onFormDataChange={handleFormDataChange}
          />

          {/* Tijdvensters & Los contact */}
          <div className="border-2 border-gray-300 p-5 bg-gray-50 mt-4">
            <h3 className="font-bold text-lg mb-4 text-gray-800">🕐 Tijdvensters & contactpersoon loslocatie</h3>
            <div className="bg-white border border-gray-200 p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Laadtijd — vanaf</label>
                  <input type="time" value={formData.laadTijdVan || ''}
                    onChange={(e) => handleFormDataChange({ laadTijdVan: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 focus:ring-2 focus:ring-blue-300" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Laadtijd — tot</label>
                  <input type="time" value={formData.laadTijdTot || ''}
                    onChange={(e) => handleFormDataChange({ laadTijdTot: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 focus:ring-2 focus:ring-blue-300" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lostijd — vanaf</label>
                  <input type="time" value={formData.losTijdVan || ''}
                    onChange={(e) => handleFormDataChange({ losTijdVan: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 focus:ring-2 focus:ring-blue-300" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lostijd — tot (bijv. voor 12:00)</label>
                  <input type="time" value={formData.losTijdTot || ''}
                    onChange={(e) => handleFormDataChange({ losTijdTot: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 focus:ring-2 focus:ring-blue-300" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contactpersoon loslocatie</label>
                  <input type="text" value={formData.losContactpersoon || ''}
                    onChange={(e) => handleFormDataChange({ losContactpersoon: e.target.value })}
                    placeholder="Naam contactpersoon"
                    className="w-full px-3 py-2.5 border border-gray-300 focus:ring-2 focus:ring-blue-300" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefoonnummer loslocatie</label>
                  <input type="tel" value={formData.losTelefoon || ''}
                    onChange={(e) => handleFormDataChange({ losTelefoon: e.target.value })}
                    placeholder="+31 6 12345678"
                    className="w-full px-3 py-2.5 border border-gray-300 focus:ring-2 focus:ring-blue-300" />
                </div>
              </div>
            </div>
          </div>

          {/* Validation errors for address fields */}
          {(validationErrors.laadPlaats || validationErrors.laadLand ||
            validationErrors.losPlaats || validationErrors.losLand) && (
            <div className="p-3 bg-red-50 border border-red-200 text-sm text-red-700">
              <strong>Adresvelden:</strong>{' '}
              {[
                validationErrors.laadPlaats,
                validationErrors.laadLand,
                validationErrors.losPlaats,
                validationErrors.losLand
              ].filter(Boolean).join(', ')}
            </div>
          )}

          {/* Goederen & Services Panel */}
          <CustomerGegevensPanel
            goederen={goederen}
            services={services}
            onGoederenChange={handleGoederenChange}
            onServiceChange={handleServiceChange}
          />

          {/* Validation error for goederen */}
          {validationErrors.goederen && (
            <div className="p-3 bg-red-50 border border-red-200 text-sm text-red-700">
              {validationErrors.goederen}
            </div>
          )}

          {/* Afmetingen validatie fouten */}
          {Object.entries(validationErrors)
            .filter(([key]) => key.startsWith('goed_afmeting_'))
            .length > 0 && (
            <div className="p-3 bg-orange-50 border border-orange-200 text-sm text-orange-800">
              <div className="font-semibold mb-1">⚠️ Vul alle afmetingen in (lengte × breedte × hoogte):</div>
              {Object.entries(validationErrors)
                .filter(([key]) => key.startsWith('goed_afmeting_'))
                .map(([key, msg]) => (
                  <div key={key} className="ml-2">• {msg}</div>
                ))
              }
            </div>
          )}

          {/* Overige Informatie Panel */}
          <div className="border-2 border-gray-300 p-5 bg-gray-50">
            <h3 className="font-bold text-lg mb-4 text-gray-800 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-900" />
              Zendingdetails
            </h3>
            <div className="bg-white border border-gray-200 p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Goederenwaarde — verplicht of optioneel op basis van klantprofiel */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Goederenwaarde (€)
                    {klantProfiel.goederenwaardeVerplicht && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.goederenwaarde || ''}
                    onChange={(e) => handleFormDataChange({ goederenwaarde: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 focus:ring-2 focus:ring-blue-300 transition-all"
                  />
                </div>

                {/* B2C */}
                <div className="flex items-center pt-6">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isB2C === true}
                      onChange={(e) => handleFormDataChange({ isB2C: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Levering bij particulier (B2C)
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Overige Informatie Panel */}
          <div className="border-2 border-gray-300 p-5 bg-gray-50">
            <h3 className="font-bold text-lg mb-4 text-gray-800 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-900" />
              Overige Informatie
            </h3>
            
            <div className="bg-white border border-gray-200 p-4 space-y-4">
              {/* Referenties */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Uw referentie
                  </label>
                  <input
                    type="text"
                    value={formData.referentie1}
                    onChange={(e) => handleFormDataChange({ referentie1: e.target.value })}
                    placeholder="Bijv. ordernummer, projectnaam..."
                    className="w-full px-3 py-2.5 border border-gray-300 focus:ring-2 focus:ring-blue-300 focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Extra referentie
                  </label>
                  <input
                    type="text"
                    value={formData.referentie2}
                    onChange={(e) => handleFormDataChange({ referentie2: e.target.value })}
                    placeholder="Optionele tweede referentie"
                    className="w-full px-3 py-2.5 border border-gray-300 focus:ring-2 focus:ring-blue-300 focus:border-transparent transition-all"
                  />
                </div>
              </div>

              {/* Opmerkingen */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Opmerkingen / speciale wensen
                </label>
                <textarea
                  value={formData.opmerkingen}
                  onChange={(e) => handleFormDataChange({ opmerkingen: e.target.value })}
                  rows="3"
                  placeholder="Eventuele aanvullende informatie of speciale wensen voor dit transport..."
                  className="w-full px-3 py-2.5 border border-gray-300 focus:ring-2 focus:ring-blue-300 focus:border-transparent transition-all resize-none"
                />
              </div>
            </div>
          </div>


          {/* ========== VASTE ZENDING TOGGLE ========== */}
          <div style={{ border:'2px solid #d4a848', borderRadius:12, padding:20, background:'#fffdf5', marginTop:8 }}>
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom: isVasteZending ? 20 : 0 }}>
              <input
                type="checkbox"
                id="isVasteZending"
                checked={isVasteZending}
                onChange={e => setIsVasteZending(e.target.checked)}
                style={{ width:18, height:18, cursor:'pointer', accentColor:'#d4a848' }}
              />
              <label htmlFor="isVasteZending" style={{ fontWeight:700, fontSize:15, color:'#1a364c', cursor:'pointer' }}>
                🔁 Dit is een vaste (periodieke) zending
              </label>
            </div>

            {isVasteZending && (
              <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
                {/* Frequentie */}
                <div>
                  <div style={{ fontSize:12, fontWeight:700, color:'#64748b', marginBottom:8, textTransform:'uppercase', letterSpacing:'.05em' }}>Herhaalfrequentie</div>
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                    {[['dagelijks','Dagelijks'],['wekelijks','Wekelijks'],['maandelijks','Maandelijks']].map(([val,lbl]) => (
                      <button key={val} type="button" onClick={() => setVasteFrequentie(val)}
                        style={{ padding:'8px 18px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer',
                          border: `2px solid ${vasteFrequentie===val ? '#1a364c' : '#e2e8f0'}`,
                          background: vasteFrequentie===val ? '#1a364c' : '#fff',
                          color: vasteFrequentie===val ? '#fff' : '#64748b' }}>
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Wekelijkse dagen */}
                {vasteFrequentie === 'wekelijks' && (
                  <div>
                    <div style={{ fontSize:12, fontWeight:700, color:'#64748b', marginBottom:8, textTransform:'uppercase', letterSpacing:'.05em' }}>Op welke dag(en)?</div>
                    <div style={{ display:'flex', gap:6 }}>
                      {['Ma','Di','Wo','Do','Vr','Za','Zo'].map((dag, i) => {
                        const key = ['maandag','dinsdag','woensdag','donderdag','vrijdag','zaterdag','zondag'][i];
                        const actief = vasteWekelijkseDagen.includes(key);
                        return (
                          <button key={key} type="button"
                            onClick={() => setVasteWekelijkseDagen(prev =>
                              actief ? prev.filter(d=>d!==key) : [...prev, key]
                            )}
                            style={{ width:40, height:40, borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer',
                              border:`2px solid ${actief ? '#1a364c' : '#e2e8f0'}`,
                              background: actief ? '#1a364c' : '#fff',
                              color: actief ? '#fff' : '#64748b' }}>
                            {dag}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Maandelijkse datum */}
                {vasteFrequentie === 'maandelijks' && (
                  <div>
                    <div style={{ fontSize:12, fontWeight:700, color:'#64748b', marginBottom:8, textTransform:'uppercase', letterSpacing:'.05em' }}>Op de ... e van de maand</div>
                    <input type="number" min={1} max={28} value={vasteMaandelijkseDatum}
                      onChange={e => setVasteMaandelijkseDatum(parseInt(e.target.value)||1)}
                      style={{ padding:'8px 12px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:14, width:80 }} />
                  </div>
                )}

                {/* Email melding */}
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <input type="checkbox" id="vasteEmail" checked={vasteEmailMelding}
                    onChange={e => setVasteEmailMelding(e.target.checked)}
                    style={{ width:16, height:16, cursor:'pointer', accentColor:'#1a364c' }} />
                  <label htmlFor="vasteEmail" style={{ fontSize:13, color:'#475569', cursor:'pointer' }}>
                    📧 Email ontvangen als er automatisch een aanvraag wordt aangemaakt
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* ========== ACTION BUTTONS ========== */}
          <div className="flex flex-col sm:flex-row gap-4 pt-4">
            <button
              type="submit"
              disabled={loading || success}
              className={`
                flex-1 py-4 px-6 font-bold text-white text-lg
                flex items-center justify-center gap-3 transition-all
                ${loading || success
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                }
              `}
            >
              {loading ? (
                <>
                  <Loader className="w-6 h-6 animate-spin" />
                  <span>Bezig met indienen...</span>
                </>
              ) : success ? (
                <>
                  <CheckCircle className="w-6 h-6" />
                  <span>Aanvraag ingediend!</span>
                </>
              ) : (
                <>
                  <Send className="w-6 h-6" />
                  <span>Aanvraag Indienen</span>
                </>
              )}
            </button>

            <button
              type="button"
              onClick={handleReset}
              disabled={loading || success}
              className="px-6 py-4 font-semibold text-gray-700 bg-white border-2 border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Formulier legen
            </button>
          </div>

          {/* Help tekst */}
          <div className="text-center text-sm text-gray-500 pt-4">
            <p>
              Na het indienen ontvang je zo snel mogelijk een prijsvoorstel van ons.
              <br />
              Vragen? Neem contact op via{' '}
              <a href="mailto:info@sendwell.nl" className="text-blue-900 hover:text-blue-700 font-medium">
                info@sendwell.nl
              </a>
            </p>
          </div>
        </form>

      </main>
    </>
  );

  // Modal modus (aangeroepen vanuit dashboard)
  if (onClose) {
    return (
      <>
        <div
          className="fixed inset-0"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}
        />
        <div
          className="fixed bg-white flex flex-col"
          style={{
            zIndex: 10001,
            left: dragPos.x,
            top: dragPos.y,
            width: Math.min(1100, window.innerWidth * 0.95),
            height: Math.min(window.innerHeight * 0.90, window.innerHeight - 80),
            boxShadow: '0 12px 48px rgba(26,54,76,0.35)',
            userSelect: isDragging ? 'none' : 'auto'
          }}
        >
          <div
            className="flex items-center justify-between px-6 py-3 border-b flex-shrink-0"
            style={{ backgroundColor: '#1a364c', cursor: 'move' }}
            onMouseDown={handleDragStart}
          >
            <span className="text-white font-bold text-lg">📦 Nieuwe Aanvraag</span>
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', color: 'white', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}
            >✕</button>
          </div>
          <div className="flex-1 overflow-y-auto" style={{ backgroundColor: '#f1f5f9' }}>
            {formContent}
          </div>
        </div>
      </>
    );
  }

  // Standalone pagina modus
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-gray-100 to-slate-200">
      <header className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/customer/dashboard')} className="p-2 hover:bg-gray-100 transition-colors">
                <ChevronLeft className="w-6 h-6 text-gray-700" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Nieuwe Aanvraag</h1>
                <p className="text-sm text-gray-600">Vraag een transport aan</p>
              </div>
            </div>
            <button onClick={() => navigate('/customer/dashboard')} className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 transition-colors">
              <Home className="w-5 h-5" />
              <span className="hidden sm:inline">Dashboard</span>
            </button>
          </div>
        </div>
      </header>
      {formContent}
    </div>
  );
};

export default CustomerNewRequest;
